import autobind from "autobind-decorator";
import type { CommonOption, FormInputProps } from "bm-ui-kit";
import classNames from "classnames";
import type { ReactNode } from "react";
import { FormCheck as BootstrapFormCheck } from "react-bootstrap";
import type { FieldRenderProps } from "react-final-form";
import { __ } from "../../utils/i18n";
import type { FormItemState } from "./FormItem";
import { FormItem } from "./FormItem";

export interface FormSegmentedControlOption<Type = string, LabelType extends string | ReactNode = string>
	extends CommonOption<Type, LabelType> {
	id?: string;
	prepend?: ReactNode;
	append?: ReactNode;
}
export type FormSegmentedControlOptions<
	Type = string,
	LabelType extends string | ReactNode = string,
> = FormSegmentedControlOption<Type, LabelType>[];

export interface FormSegmentedControlProps<Type = string> extends FormInputProps<Type> {
	options: FormSegmentedControlOptions<Type, ReactNode>;
	fullWidth?: boolean;
}

export class FormSegmentedControl<FieldType = string> extends FormItem<
	FieldType,
	FormSegmentedControlProps<FieldType>,
	FormItemState
> {
	@autobind
	protected handleValidate(value: FieldType, allValues: unknown): string {
		const { validate, required } = this.props;

		if (validate) {
			return validate(value, allValues);
		}

		if (required && (value === undefined || value === null || value === "")) {
			return __("Kérjük válassz ki egy elemet!");
		}
	}

	protected renderField(fieldProps: FieldRenderProps<FieldType, HTMLElement, FieldType>): ReactNode {
		const { size, options, readOnly, tabIndex, name, fullWidth } = this.props;
		const {
			input: { value },
		} = fieldProps;

		const className = classNames(
			"form-segmented-control",
			size && `form-segmented-control-${size}`,
			fullWidth && "full-width",
		);

		return (
			<div>
				<div className={className}>
					{options.map((option: FormSegmentedControlOption<FieldType>, key) => (
						<BootstrapFormCheck
							type="radio"
							key={option.id || `${this.id}-${key}`}
							id={option.id || `${this.id}-${key}`}
							className={classNames(
								"form-segmented-control-item",
								(this.isDisabled() || option.disabled) && "disabled",
							)}
							readOnly={readOnly}
							disabled={this.isDisabled() || option.disabled}
							tabIndex={tabIndex}
							name={name}
							value={String(option.value)}
							label={this.renderItemLabel(option)}
							checked={`${value}` === `${option.value}`}
							onChange={() => {
								this.handleChange(option.value, fieldProps);
							}}
							onFocus={(event) => this.handleFocus(event, fieldProps)}
							onBlur={(event) => this.handleBlur(event, fieldProps)}
						/>
					))}
				</div>
			</div>
		);
	}

	private renderItemLabel(option: FormSegmentedControlOption<FieldType>): ReactNode {
		const { label, prepend, append } = option;
		const className = classNames(
			"form-segmented-control-item-label",
			prepend && "has-prepend",
			append && "has-append",
		);

		return (
			<span className={className}>
				{prepend && <span className="prepend">{prepend}</span>}
				{label}
				{append && <span className="append">{append}</span>}
			</span>
		);
	}
}
